import { useEffect, useMemo, useState } from "react";
import { format, sub, startOfMonth, endOfMonth } from "date-fns";
import DatePicker from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import { Alert, Card, Drawer, Icon, Tabs } from "barte-design-system";
import { CircleIcon, RadioButtonIcon } from "@phosphor-icons/react";
import getAPIReport from "@/services/Api/reportApiConfig";
import { getAPIN8N } from "@/services/Api/n8nApiConfig";
import { showToast } from "@/utils";
import { useAsyncFunction } from "@/hooks/useAsyncFunction";
import { useReport } from "@/hooks";
import { Select } from "@/components";
import { renderCalendarHeader, renderMonthCalendarHeader } from "@shared/components";
import styles from "./relatorios.module.css";
import type { PeriodType, GenerateReportModalProps } from "./generate-report-modal.type";

const reportApi = getAPIReport();
const n8nApi = getAPIN8N();

export function GenerateReportModal({
    isOpen,
    onClose,
    onCloseSuccess,
    reportType,
}: GenerateReportModalProps) {
    const accountOptions = [
        { label: "Conta de débito", value: "CHECKING_ACCOUNT" },
        { label: "Conta de investimento", value: "INVESTMENT_ACCOUNT" },
    ];

    const [periodType, setPeriodType] = useState<PeriodType>("today");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedAccountType, setSelectedAccountType] = useState("CHECKING_ACCOUNT");
    const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);

    const [user] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("barte-user-info") ?? "{}");
        } catch {
            return {};
        }
    });
    const [impersonator] = useState<string | null>(() => localStorage.getItem("barte-impersonator"));
    const { generateBankingReport } = useReport();
    const { execute } = useAsyncFunction(generateBankingReport);
    const [isLoading, setIsLoading] = useState(false);

    const todayDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => sub(todayDate, { days: 90 }), [todayDate]);
    const isTaxCommissions = reportType?.query_file === "tax_commissions";

    useEffect(() => {
        if (isOpen) {
            setPeriodType("today");
            setStartDate(null);
            setEndDate(null);
            setSelectedAccountType("CHECKING_ACCOUNT");
            setSelectedMonth(isTaxCommissions ? new Date() : null);
        }
    }, [isOpen, isTaxCommissions]);

    async function handleGenerateReport() {
        if (!reportType) return;

        if (isTaxCommissions && !selectedMonth) {
            showToast({ type: "warning", message: "Selecione um mês para gerar o relatório." });
            return;
        }

        const today = new Date();
        const resolvedStartDate = periodType === "today" ? today : startDate;
        const resolvedEndDate = periodType === "today" ? today : endDate;

        if (!isTaxCommissions && (!resolvedStartDate || !resolvedEndDate)) {
            showToast({ type: "warning", message: "Selecione um período para gerar o relatório." });
            return;
        }

        setIsLoading(true);
        try {
            if (isTaxCommissions) {
                const formData = {
                    report_type: reportType.query_file,
                    recipient_email: impersonator ?? user?.email,
                    start_date: `${format(startOfMonth(selectedMonth!), "yyyy-MM-dd")}T00:00:00`,
                    end_date: `${format(endOfMonth(selectedMonth!), "yyyy-MM-dd")}T23:59:00`,
                };
                await n8nApi.post("tax_commissions", formData);
            } else if (reportType.query_file === "banking_account_statement") {
                const formData = {
                    start_date: format(resolvedStartDate!, "yyyy-MM-dd"),
                    end_date: format(resolvedEndDate!, "yyyy-MM-dd"),
                    account_type: selectedAccountType,
                };
                await execute(formData);
            } else {
                const formData = {
                    report_type: reportType.query_file,
                    recipient_email: impersonator ?? user?.email,
                    start_date: `${format(resolvedStartDate!, "yyyy-MM-dd")}T00:00:00`,
                    end_date: `${format(resolvedEndDate!, "yyyy-MM-dd")}T23:59:00`,
                };
                await reportApi.post("", formData);
            }
            showToast({ type: "success", message: "Relatório gerado. Em breve você receberá um email." });
            onCloseSuccess();
        } catch {
            showToast({ type: "error", message: "Falha ao tentar gerar o relatório." });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Drawer
            title={reportType?.title ?? "Gerar relatório"}
            isOpen={isOpen}
            onClose={onClose}
            footer={{
                primaryButton: {
                    children: "Gerar relatório",
                    onClick: handleGenerateReport,
                    loading: isLoading,
                    disabled: isLoading,
                },
                secondaryButton: {
                    children: "Cancelar",
                    variant: "outline",
                    onClick: onClose,
                },
            }}
        >
            <div className={styles.drawerContent}>
                <div className={styles.drawerPeriodHeader}>
                    <p className={styles.drawerPeriodTitle}>Selecione o período</p>
                </div>

                {!isTaxCommissions && (
                    <Tabs className={styles.periodSelector}>
                        <Tabs.Tab
                            active={periodType === "today"}
                            icon={<Icon icon={periodType === "today" ? RadioButtonIcon : CircleIcon} />}
                            onClick={() => setPeriodType("today")}
                        >
                            Hoje
                        </Tabs.Tab>
                        <Tabs.Tab
                            active={periodType === "period"}
                            icon={<Icon icon={periodType === "period" ? RadioButtonIcon : CircleIcon} />}
                            onClick={() => setPeriodType("period")}
                        >
                            Período
                        </Tabs.Tab>
                    </Tabs>
                )}

                <Card>
                    <div className={styles.calendarWrapper}>
                        {isTaxCommissions ? (
                            <DatePicker
                                selected={selectedMonth}
                                onChange={(date: Date | null) => setSelectedMonth(date)}
                                showMonthYearPicker
                                inline
                                locale={ptBR}
                                calendarClassName={styles.calendar}
                                renderCustomHeader={renderMonthCalendarHeader}
                            />
                        ) : (
                            <>
                                {periodType === "period" && (
                                    <div className={styles.dateInputsRow}>
                                        <div className={styles.dateInputBox}>
                                            {startDate ? format(startDate, "dd/MM/yy") : "--/--/--"}
                                        </div>
                                        <span className={styles.dateInputSeparator}>-</span>
                                        <div className={styles.dateInputBox}>
                                            {endDate ? format(endDate, "dd/MM/yy") : "--/--/--"}
                                        </div>
                                    </div>
                                )}

                                {periodType === "today" ? (
                                    <DatePicker
                                        selected={todayDate}
                                        onChange={() => {}}
                                        inline
                                        locale={ptBR}
                                        calendarClassName={`${styles.calendar} ${styles.calendarReadOnly}`}
                                        renderCustomHeader={renderCalendarHeader}
                                    />
                                ) : (
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={([start, end]) => {
                                            setStartDate(start);
                                            setEndDate(end);
                                        }}
                                        inline
                                        locale={ptBR}
                                        calendarClassName={styles.calendar}
                                        renderCustomHeader={renderCalendarHeader}
                                        maxDate={new Date()}
                                        minDate={minDate}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Card>

                {reportType?.query_file === "banking_account_statement" && (
                    <Select
                        name="accountType"
                        label="Escolha o tipo de conta"
                        optionsList={accountOptions.map((option) => ({
                            title: option.label,
                            value: option.value,
                        }))}
                        onChange={(value) => setSelectedAccountType(value)}
                        placeholder="Selecione o tipo de conta"
                    />
                )}

                <div className={styles.alertWrapper}>
                    <Alert
                        variant="info"
                        title="Envio por email"
                        showCloseButton={false}
                        description={`O relatório será enviado para: ${user?.email}`}
                    />
                </div>
            </div>
        </Drawer>
    );
}
